/**
 * The in-game attribute taxonomy — the canonical key -> label/short/category
 * table, shared by both apps.
 *
 * It lived in web's `redux/squadBuilder/squadBuilderUtils.ts`, and the mobile
 * compare screen re-typed all 29 entries by hand, which had already drifted
 * (SPD/JUM/STM vs SPR/JMP/STA, and mobile's "STA" for standingTackles collided
 * with web's for stamina). Deriving both apps from this one table is what stops
 * a renamed stat from having to be edited in two shapes.
 */
export const categoryToUniqueKey = {
    PACE: "pace",
    SHOOTING: "shooting",
    PASSING: "passing",
    DRIBBLING: "dribblingTitle",
    DEFENDING: "defending",
    PHYSICALITY: "physicality",
    GOALKEEPING: "goalkeeping",
};
export const inGameAttributesDict = {
    pace: { label: "PACE", short: "PAC", category: "PACE" },
    dribblingTitle: { label: "DRIBBLING", short: "DRI", category: "DRIBBLING" },
    physicality: { label: "PHYSICALITY", short: "PHY", category: "PHYSICALITY" },
    defending: { label: "DEFENDING", short: "DEF", category: "DEFENDING" },
    shooting: { label: "SHOOTING", short: "SHO", category: "SHOOTING" },
    passing: { label: "PASSING", short: "PAS", category: "PASSING" },
    acceleration: { label: "Acceleration", short: "ACC", category: "PACE" },
    sprintSpeed: { label: "Sprint Speed", short: "SPR", category: "PACE" },
    agility: { label: "Agility", short: "AGI", category: "DRIBBLING" },
    balance: { label: "Balance", short: "BAL", category: "DRIBBLING" },
    jumping: { label: "Jumping", short: "JMP", category: "PHYSICALITY" },
    stamina: { label: "Stamina", short: "STA", category: "PHYSICALITY" },
    strength: { label: "Strength", short: "STR", category: "PHYSICALITY" },
    reactions: { label: "Reactions", short: "REA", category: "DRIBBLING" },
    aggression: { label: "Aggression", short: "AGG", category: "PHYSICALITY" },
    composure: { label: "Composure", short: "COM", category: "DRIBBLING" },
    interceptions: {
        label: "Interceptions",
        short: "INT",
        category: "DEFENDING",
    },
    positioning: { label: "Positioning", short: "POS", category: "SHOOTING" },
    vision: { label: "Vision", short: "VIS", category: "PASSING" },
    ballControl: { label: "Ball Control", short: "CTRL", category: "DRIBBLING" },
    crossing: { label: "Crossing", short: "CRO", category: "PASSING" },
    dribbling: { label: "Dribbling", short: "DRI", category: "DRIBBLING" },
    finishing: { label: "Finishing", short: "FIN", category: "SHOOTING" },
    freeKickAccuracy: { label: "FK Accuracy", short: "FK", category: "PASSING" },
    headingAccuracy: {
        label: "Heading Accuracy",
        short: "HEA",
        category: "DEFENDING",
    },
    longPassing: { label: "Long Passing", short: "L.PAS", category: "PASSING" },
    shortPassing: { label: "Short Passing", short: "S.PAS", category: "PASSING" },
    defensiveAwareness: {
        label: "Def. Awareness",
        short: "AWA",
        category: "DEFENDING",
    },
    shotPower: { label: "Shot Power", short: "POW", category: "SHOOTING" },
    longShots: { label: "Long Shots", short: "L.SHO", category: "SHOOTING" },
    standingTackles: {
        label: "Standing Tackles",
        short: "S.TCK",
        category: "DEFENDING",
    },
    slidingTackles: {
        label: "Sliding Tackles",
        short: "SL.TCK",
        category: "DEFENDING",
    },
    volleys: { label: "Volleys", short: "VOL", category: "SHOOTING" },
    curve: { label: "Curve", short: "CUR", category: "PASSING" },
    penalties: { label: "Penalties", short: "PEN", category: "SHOOTING" },
    gkDiving: { label: "GK Diving", short: "DIV", category: "GOALKEEPING" },
    gkHandling: { label: "GK Handling", short: "HAN", category: "GOALKEEPING" },
    gkKicking: { label: "GK Kicking", short: "KIC", category: "GOALKEEPING" },
    gkReflexes: { label: "GK Reflexes", short: "REF", category: "GOALKEEPING" },
    gkPositioning: {
        label: "GK Positioning",
        short: "POS",
        category: "GOALKEEPING",
    },
};
/**
 * `playerattributes` keys of the six card face stats, in `player.attributes`
 * index order, per player type. Cards read their labels by these keys.
 */
export const FACE_STAT_KEYS = {
    outfield: [
        "pace",
        "shooting",
        "passing",
        "dribblingTitle",
        "defending",
        "physicality",
    ],
    gk: [
        "diving",
        "handling",
        "kicking",
        "reflexes",
        "speed",
        "positioningTitle",
    ],
};
/**
 * The in-game attributes a step response may carry as their own deltas, in
 * the order the builder lists them.
 */
export const INDIVIDUAL_ATTRIBUTE_KEYS = [
    "acceleration",
    "sprintSpeed",
    "agility",
    "balance",
    "reactions",
    "composure",
    "ballControl",
    "dribbling",
    "jumping",
    "stamina",
    "strength",
    "aggression",
    "interceptions",
    "headingAccuracy",
    "defensiveAwareness",
    "standingTackles",
    "slidingTackles",
    "positioning",
    "finishing",
    "shotPower",
    "longShots",
    "volleys",
    "penalties",
    "vision",
    "crossing",
    "freeKickAccuracy",
    "longPassing",
    "shortPassing",
    "curve",
    "gkDiving",
    "gkHandling",
    "gkKicking",
    "gkReflexes",
    "gkPositioning",
];
/** The six outfield overalls, in `player.attributes` index order. */
export const ATTRIBUTE_CATEGORIES = [
    { short: "PAC", category: "PACE" },
    { short: "SHO", category: "SHOOTING" },
    { short: "PAS", category: "PASSING" },
    { short: "DRI", category: "DRIBBLING" },
    { short: "DEF", category: "DEFENDING" },
    { short: "PHY", category: "PHYSICALITY" },
];
// The dict also holds the category-title pseudo-attributes (pace,
// dribblingTitle, ...) — those are the overall values, not sub-attributes.
const TITLE_KEYS = new Set(Object.values(categoryToUniqueKey));
/** A category's real sub-attributes, titles excluded, in dict order. */
export const inGameAttrsForCategory = (category) => Object.entries(inGameAttributesDict)
    .filter(([key, def]) => def.category === category && !TITLE_KEYS.has(key))
    .map(([key, def]) => ({ key, label: def.label, short: def.short }));
//# sourceMappingURL=playerAttributes.js.map